import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

/**
 * Orca project status — lifecycle states for an orchestrated project walk.
 * @since 1.0.0
 */
export const OrcaProjectStatus = Schema.Literal(
  "PENDING",
  "ACTIVE",
  "COMPLETED",
  "BLOCKED",
  "CANCELED",
);
export type OrcaProjectStatus = Schema.Schema.Type<typeof OrcaProjectStatus>;

/**
 * Orca verify-gate status — the lifecycle of the project-level verify gate that
 * runs e2e against the project's preview environment when the walk completes,
 * BEFORE the integration-branch roll-up PR opens (INF-774). Modeled as a
 * nullable varchar column (not a pgEnum) so the vocabulary can grow without
 * tripping the enum-value-isolation two-deploy trap. NULL = the project never
 * entered the verify gate (verify disabled, or no integration branch).
 *
 * - PENDING   — reserved for a future queued state; not written today.
 * - RUNNING   — a verify workflow has been dispatched and the verdict is awaited.
 * - PASSED    — the verify gate passed; the roll-up has been (or is being) opened.
 * - FAILED    — the verify gate failed; the roll-up is withheld (red-loop territory).
 * - EXHAUSTED — bounded redispatch was exhausted without a verdict; treated as failed.
 *
 * @since 1.0.0
 */
export const OrcaVerifyStatus = Schema.Literal(
  "PENDING",
  "RUNNING",
  "PASSED",
  "FAILED",
  "EXHAUSTED",
);
export type OrcaVerifyStatus = Schema.Schema.Type<typeof OrcaVerifyStatus>;

/**
 * OrcaProject model — one row per Linear project that has transitioned to
 * "Ready for AI". Acts as the orchestration root for project-level scheduling
 * across the project's issues.
 *
 * @since 1.0.0
 */
export class OrcaProject extends Model.Class<OrcaProject>("OrcaProject")({
  id: Model.Generated(Schema.String),

  linearProjectId: Schema.String,
  status: OrcaProjectStatus,

  repo: Schema.NullOr(Schema.String),

  integrationBranch: Schema.NullOr(Schema.String),

  notifyChannel: Schema.NullOr(Schema.String),
  notifyUserId: Schema.NullOr(Schema.String),

  startedAt: Schema.NullOr(Schema.DateTimeUtc),
  completedAt: Schema.NullOr(Schema.DateTimeUtc),
  blockedAt: Schema.NullOr(Schema.DateTimeUtc),
  lastSweptAt: Schema.NullOr(Schema.DateTimeUtc),
  linearProjectClosedAt: Schema.NullOr(Schema.DateTimeUtc),

  errorMessage: Schema.NullOr(Schema.String),

  maxParallel: Schema.Number,

  verifyStatus: Schema.NullOr(OrcaVerifyStatus),
  verifyWorkflowRunId: Schema.NullOr(Schema.Number),
  verifyDispatchPendingAt: Schema.NullOr(Schema.DateTimeUtc),
  verifyRedispatchCount: Schema.Number,
  verifyCycles: Schema.Number,
  verifyFindingKeys: Schema.Array(Schema.String),

  rollupPrNumber: Schema.NullOr(Schema.Number),
  rollupPrUrl: Schema.NullOr(Schema.String),

  graphVerifiedAt: Schema.NullOr(Schema.DateTimeUtc),

  /**
   * OL-60: the customer that owns this project; `null` = unowned legacy row.
   *
   * @since 1.0.0
   */
  customerId: Schema.NullOr(Schema.String),

  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
}) {}
